import { trapFocus, removeTrapFocus } from "@theme/focus";
class ScxModal extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });

    if (!document.querySelector('script[src*="trustpilot.com"]')) {
      const script = document.createElement('script');
      script.src = "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
      script.async = true;
      document.head.appendChild(script);
    }

    const wrapper = document.createElement("div");
    wrapper.innerHTML = `
        <style>
          :host { 
            display: contents;
            position: fixed !important;
            inset: 0 !important;
            z-index: 1000 !important;
          }
  
          * {
            margin: 0;
            padding: 0;
          }

          .modal {
            position: fixed;
            inset: 0;
            display: none;
            pointer-events: none;
            z-index: 1000;
          }

          .sticky {
            position: sticky;
            bottom: 0;
            margin-top: auto;
        }
          
        .modal-backdrop {
          position: absolute;
          inset:0;
          background: rgba(0, 0, 0, 0.4);
          opacity: 0;
            transition: opacity 0.3s ease-out;
        }

        .modal.open .modal-backdrop {
          opacity: 1;
        }

          .modal.open {
            opacity: 1;
            pointer-events: auto;
          }
  
          .modal-content {
            transition: transform 0.3s ease-out;
            outline: none;
          }
  
          /* === DIALOG STYLE === */
          .dialog .modal-content {
            margin: auto;
            padding: 2rem;
            max-width: 500px;
            width: 90%;
            border-radius: var(--modal-radius);
            transform: translateY(-20px) scale(0.98);
            opacity: 0;
          }
  
          .modal.open.dialog .modal-content {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
  
          /* === DRAWER STYLE === */
          .drawer {
            align-items: stretch;
          }
  
          .drawer .modal-content {
            width: 300px;
            height: 100vh;
            opacity: 1;
            transform: translateX(-100%);
          }
  
          .drawer.right .modal-content {
            gap: var(--gap-lg);
            margin-left: auto;
            transform: translateX(100%);
          }
  
          .modal.open.drawer.left .modal-content {
            transform: translateX(0);
          }
  
          .modal.open.drawer.right .modal-content {
            transform: translateX(0);
          }
  
          .close {
            background: transparent;
            border: none;
            cursor: pointer;
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            width: 100%;
          }

          .close button {
            background: transparent;
            border: none;
          }

          @media (max-width: 768px) {
            .drawer .modal-content {
              width: 100vw;
              max-width: 100vw;
              height: auto;
              max-height: 90vh;
              border-radius: 1.5rem 1.5rem 0 0;
              margin: 0 auto 0 0;
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
              transform: translateY(100%) !important;
              transition: transform 0.3s cubic-bezier(.4,0,.2,1);
              height: 100%;
            }
            .modal.open.drawer .modal-content {
              transform: translateY(0) !important;
            }
          }
            
        </style>
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div class="modal-backdrop" part="backdrop"></div>
          <div class="modal-content" tabindex="-1" part="wrapper">
            <div part="title">
              <div part="close" class="close" aria-label="Close"><slot name="close"></slot></div>
              <h2 part="title-text" id="modal-title"><slot name="title"></slot></h2>
            </div>
            <div part="content"><slot part="content-inside" name="content"></slot></div>
            <div class="sticky" part="sticky"><slot name="sticky"></slot></div>
          </div>
        </div>
      `;

    if (!this.shadowRoot) return;
    this.shadowRoot.appendChild(wrapper);
    this.modalEl = this.shadowRoot.querySelector(".modal");
    this.contentEl = this.shadowRoot.querySelector(".modal-content");
    this.closeBtn = this.shadowRoot.querySelector(".close");
    this.backdrop = this.shadowRoot.querySelector(".modal-backdrop");
    this.previouslyFocused = null;
    this.isMovedToBody = false;

    this._onTransitionEnd = (event) => {
      if (
        event.propertyName === "transform" &&
        !this.modalEl.classList.contains("open")
      ) {
        this.modalEl.style.display = "none";
      }
    }; 
    this.contentEl.addEventListener("transitionend", this._onTransitionEnd);

    // Move to body immediately if DOM is ready
    this._moveToBody();
  }

  _moveToBody() {
    // Ensure the modal is always a direct child of body
    if (this.parentNode !== document.body && !this.isMovedToBody) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (document.body && this.parentNode !== document.body) {
          document.body.appendChild(this);
          this.isMovedToBody = true;
        }
      });
    }
  }

  connectedCallback() {
    // Ensure modal is moved to body when connected
    this._moveToBody();

    this.closeBtn.addEventListener("click", () => this.close());
    this.backdrop.addEventListener("click", () => this.close());

    this._escHandler = (e) => {
      if (e.key === "Escape") this.close();
    };

    this._tabHandler = (e) => this._trapFocus(e);

    this._applyMode();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._escHandler);
    document.removeEventListener("keydown", this._tabHandler);
  }

  _applyMode() {
    const type = this.getAttribute("type") || "dialog";
    const side = this.getAttribute("side") || "left";

    this.modalEl.classList.remove("dialog", "drawer", "left", "right");

    if (type === "drawer") {
      this.modalEl.classList.add("drawer", side);
    } else {
      this.modalEl.classList.add("dialog");
    }
  }

  open() {
    this._applyMode();
    this._moveToBody();
    
    requestAnimationFrame(() => {
      this.modalEl.style.display = "flex";
      void this.modalEl.offsetWidth;
      this.modalEl.classList.add("open");
      document.body.classList.add("drawer-container");

      const tpWidget = this.shadowRoot.querySelector('.trustpilot-widget');
      if (window.Trustpilot && tpWidget) {
        window.Trustpilot.loadFromElement(tpWidget);
      }

      this._a11yExpandedToggle(true);
      this.previouslyFocused = document.activeElement;
      document.addEventListener("keydown", this._escHandler);
      document.addEventListener("keydown", this._tabHandler);

      trapFocus(this.modalEl);
      requestAnimationFrame(() => {
        this.contentEl.focus();
      });
    });
  }

  close() {
    this.modalEl.classList.remove("open");
    document.body.classList.remove("drawer-container");

    this._a11yExpandedToggle(false);
    document.removeEventListener("keydown", this._escHandler);
    document.removeEventListener("keydown", this._tabHandler);

    removeTrapFocus();
    if (this.previouslyFocused) {
      this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }
  }

  _a11yExpandedToggle(state) {
    const openingButton = document.querySelector(
      `[data-modal-id='${this.id}']`
    );
    if (openingButton) {
      openingButton.setAttribute("aria-expanded", state);
    }
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  _trapFocus(event) {
    if (event.key !== "Tab") return;

    const focusables = this.shadowRoot.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const focusable = Array.from(focusables).filter(
      (el) => !el.disabled && el.offsetParent !== null
    );

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define("scx-modal", ScxModal);

class ScxModalOpener extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", () => {
      document.getElementById(this.getAttribute("data-modal-id"))?.open();
    });
  }
}

customElements.define("scx-modal-opener", ScxModalOpener);

